/**
 * <FamilyAnniversariesStrip /> — "This week in your family" widget.
 *
 * Reads /api/family/anniversaries (auth-required) and shows the coming
 * birthdays, wedding anniversaries and remembrance days for every tree
 * the user has access to. Silently hides when the user isn't signed in
 * or has no matching events in the window.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Cake, Heart, Flower2, CalendarDays, ArrowUpRight, TreeDeciduous } from "lucide-react";

const KIND_META = {
  birthday:            { Icon: Cake,       tone: { bg: "#FFF7ED", fg: "#9A3412", border: "#FED7AA" }, verb: "Happy birthday" },
  wedding_anniversary: { Icon: Heart,      tone: { bg: "#FDF2F8", fg: "#9D174D", border: "#FBCFE8" }, verb: "Wedding anniversary" },
  remembrance:         { Icon: Flower2,    tone: { bg: "#F5F3FF", fg: "#5B21B6", border: "#DDD6FE" }, verb: "In loving memory" },
  event:               { Icon: CalendarDays, tone: { bg: "#EFF6FF", fg: "#1E40AF", border: "#BFDBFE" }, verb: "Anniversary" },
};

function fmtDay(iso) {
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const dd = new Date(d); dd.setHours(0, 0, 0, 0);
  const diff = Math.round((dd - today) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

export default function FamilyAnniversariesStrip({ windowDays = 14 }) {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [publicCounts, setPublicCounts] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Always load the public aggregate — used as a fallback + teaser.
      try {
        const { data: pub } = await http.get("/family/anniversaries/public", { params: { window_days: windowDays } });
        if (!cancelled) setPublicCounts(pub || null);
      } catch { /* ignore */ }
      // Personalised list — only if signed in.
      if (!user) { if (!cancelled) setItems([]); return; }
      try {
        const { data } = await http.get("/family/anniversaries", { params: { window_days: windowDays } });
        if (!cancelled) setItems(data.items || []);
      } catch { if (!cancelled) setItems([]); }
    })();
    return () => { cancelled = true; };
  }, [user, windowDays]);

  const personal = items || [];
  const showPersonal = personal.length > 0;
  const counts = publicCounts?.counts;
  const totalPublic = counts ? (counts.birthdays + counts.weddings + counts.remembrances) : 0;
  const showPublicFallback = !showPersonal && totalPublic > 0;
  if (!showPersonal && !showPublicFallback) return null;

  return (
    <section className="my-10" data-testid="family-anniversaries-strip">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline" style={{ color: "var(--wofy-primary)" }}>
            <TreeDeciduous size={12} className="inline mr-1 -mt-0.5" />{" "}
            {showPersonal ? "THIS WEEK IN YOUR FAMILY" : `THIS WEEK ACROSS SOCIAL PULSE · ${publicCounts?.trees || 0} PUBLIC TREES`}
          </div>
          <h2 className="font-display text-2xl md:text-3xl mt-1">
            {showPersonal ? "Anniversaries · Birthdays · Remembrances" : "Every family has a story"}
          </h2>
        </div>
        <Link to="/family" className="text-sm inline-flex items-center gap-1 hover:underline" style={{ color: "var(--wofy-primary)" }}>
          Family Trees <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>

      {showPublicFallback ? (
        <div className="grid gap-3 md:grid-cols-3">
          {counts.birthdays > 0 && <PublicTeaser kind="birthday" count={counts.birthdays} />}
          {counts.weddings  > 0 && <PublicTeaser kind="wedding_anniversary" count={counts.weddings} />}
          {counts.remembrances > 0 && <PublicTeaser kind="remembrance" count={counts.remembrances} />}
          <div className="md:col-span-3 text-sm mt-2 rounded-2xl border p-4"
               style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)", color: "var(--wofy-text-soft)" }}>
            {user ? (
              <>Nothing on your family calendar this week — <Link to="/family" className="underline" style={{ color: "var(--wofy-primary)" }}>add relatives</Link> so we can remind you of their birthdays and anniversaries.</>
            ) : (
              <><Link to="/login" className="underline" style={{ color: "var(--wofy-primary)" }}>Sign in</Link> or <Link to="/family" className="underline" style={{ color: "var(--wofy-primary)" }}>create your family tree</Link> to see your own weekly digest.</>
            )}
          </div>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {personal.slice(0, 12).map((it, i) => {
            const meta = KIND_META[it.kind] || KIND_META.event;
            const Icon = meta.Icon;
            const memberHref = `/family/${it.tree_slug}/member/${it.member_id}`;
            const memorialHref = it.memorial_slug ? `/celebrate/${it.memorial_slug}` : null;
            return (
              <div
                key={i}
                className="rounded-2xl border p-4 flex items-start gap-3"
                style={{ borderColor: meta.tone.border, background: meta.tone.bg }}
                data-testid={`fam-anniv-${it.kind}-${i}`}
              >
                <div className="w-12 h-12 rounded-full overflow-hidden shrink-0 flex items-center justify-center" style={{ background: "white", border: `1px solid ${meta.tone.border}` }}>
                  {it.member_photo ? (
                    <img src={it.member_photo} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Icon className="w-5 h-5" style={{ color: meta.tone.fg }} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-mono uppercase tracking-wider" style={{ color: meta.tone.fg }}>
                    <Icon className="w-3 h-3 inline mr-1 -mt-0.5" /> {meta.verb} · {fmtDay(it.date)}
                  </div>
                  <Link to={memberHref} className="font-medium leading-tight hover:underline block truncate">{it.member_name}</Link>
                  <div className="text-xs opacity-80 truncate">
                    {it.spouse_name ? `with ${it.spouse_name} · ` : ""}{it.detail}
                  </div>
                  <div className="text-[10px] opacity-60 mt-1 flex items-center gap-2">
                    <Link to={`/family/${it.tree_slug}`} className="hover:underline">{it.tree_name}</Link>
                    {memorialHref && (
                      <Link to={memorialHref} className="hover:underline inline-flex items-center gap-1" style={{ color: meta.tone.fg }}>
                        <Flower2 className="w-3 h-3" /> Memorial
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function PublicTeaser({ kind, count }) {
  const meta = KIND_META[kind];
  const Icon = meta.Icon;
  return (
    <div className="rounded-2xl border p-4 flex items-center gap-3" style={{ borderColor: meta.tone.border, background: meta.tone.bg }}>
      <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "white", border: `1px solid ${meta.tone.border}` }}>
        <Icon className="w-5 h-5" style={{ color: meta.tone.fg }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-mono uppercase tracking-wider" style={{ color: meta.tone.fg }}>
          {meta.verb}
        </div>
        <div className="font-display text-xl leading-tight">{count} <span className="text-sm opacity-70">this week</span></div>
        <div className="text-xs opacity-70">across Social Pulse's public trees</div>
      </div>
    </div>
  );
}
